export const GUIDE_DATA = [
  {
    country: "Philippines",
    guide: [
      {
        title: "1. Country overview & strategic context",
        content: [
          {
            type: "paragraph",
            text: "The Philippines is one of the most mature and institutionally experienced global workforce markets in the world.",
          },
          {
            type: "list",
            text: "The country's workforce model is shaped by:",
            items: [
              "A highly protective labour law regime",
              "Strong English-language education",
              "Cultural familiarity with Western business norms",
              "A workforce accustomed to structured processes",
            ],
          },
          {
            type: "paragraph",
            text: "This combination makes the Philippines a low-execution-risk but higher-compliance-discipline jurisdiction.",
          },
        ],
      },

      {
        title: "2. Employment & engagement model (Philippines)",
        content: [
          {
            type: "paragraph",
            text: "Philippine labour law classifies workers based on the substance of the relationship.",
          },

          {
            type: "subsection",
            title: "2.1 Regular (full-time) employment",
            content: [
              {
                type: "paragraph",
                text: "Regular employment is the default and most protected employment status.",
              },
              {
                type: "list",
                text: "Key characteristics:",
                items: [
                  "Typically 8 hours per day",
                  "Entitled to statutory benefits",
                  "Covered by security of tenure",
                ],
              },
              {
                type: "paragraph",
                text: "Once an employee is regularised, termination becomes legally complex and high-risk if not properly documented and justified.",
              },
              {
                type: "list",
                text: "Security of tenure: A regular employee may only be terminated for:",
                items: [
                  "Just causes (e.g. serious misconduct, gross neglect, fraud), or",
                  "Authorised causes (e.g. redundancy, retrenchment, closure)",
                ],
              },
              {
                type: "paragraph",
                text: "Even where cause exists, employers must follow strict procedural steps.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.2 Probationary employment",
            content: [
              {
                type: "paragraph",
                text: "Probationary employment allows employers to assess suitability but is time-limited and regulated.",
              },
              {
                type: "list",
                text: "Rules:",
                items: [
                  "Maximum probation period: 6 months",
                  {
                    text: "Performance standards must be:",
                    items: [
                      "Reasonable",
                      "Job-related",
                      "Communicated at commencement"
                    ]
                  }
                ],
              },
              {
                type: "paragraph",
                text: "Failure to clearly document probation standards or performance issues can result in the employee being deemed regular by default, even before six months.",
              },
              {
                type: "keyValue",
                keyText: "Practical reality:",
                value: "Probation is not a \"risk-free\" period. Poorly managed probation is a common source of disputes.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.3 Fixed-term employment",
            content: [
              {
                type: "paragraph",
                text: "Fixed-term contracts are legally recognised but heavily scrutinised.",
              },
              {
                type: "list",
                text: "Accepted use cases:",
                items: [
                  "Genuine time-bound work",
                  "Temporary cover",
                  "Project-specific engagements",
                ],
              },
              {
                type: "list",
                text: "Risk factors:",
                items: [
                  "Repeated renewals for the same role",
                  "Ongoing core business functions",
                  "Long-term exclusivity",
                ],
              },
              {
                type: "paragraph",
                text: "Where a fixed-term employee performs the same role continuously, courts may deem the role regular in nature, regardless of contract wording.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.4 Project-based employment",
            content: [
              {
                type: "list",
                text: "Project employment is permitted when:",
                items: [
                  "The project is clearly defined",
                  "The scope and duration are known at commencement",
                  "Employment ends upon project completion",
                ],
              },
              {
                type: "paragraph",
                text: "Documentation at the outset is critical. Vague or open-ended \"projects\" materially increase reclassification risk.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.5 Part-time employment",
            content: [
              {
                type: "paragraph",
                text: "Part-time employment is recognised under Philippine law.",
              },
              {
                type: "list",
                text: "Key points:",
                items: [
                  "Fewer working hours than full-time employees",
                  "Statutory benefits apply pro-rata",
                  "Labour protections still apply in full",
                ],
              },
              {
                type: "paragraph",
                text: "Part-time status does not reduce termination or due-process obligations.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.6 Independent contractors / consultants",
            content: [
              {
                type: "paragraph",
                text: "Independent contracting is legally permissible but carries high enforcement risk.",
              },
              {
                type: "list",
                text: "Authorities assess:",
                items: [
                  "Degree of control and supervision",
                  "Exclusivity",
                  "Integration into business operations",
                  "Economic dependence",
                ],
              },
              {
                type: "list",
                text: "Where a contractor functions like an employee in practice, reclassification can trigger:",
                items: [
                  "Back pay",
                  "Unpaid benefits",
                  "Reinstatement claims",
                ],
              },
            ],
          },
          {
            type: "subsection",
            title: "2.7 Employment model risk summary",
            content: [
              {
                type: "tableText",
                headers: ["Engagement type", "Legal certainty", "Risk level"],
                rows: [
                  ["Regular employment", "Very high", "Low"],
                  ["Probationary", "High (time-limited)", "Low–medium"],
                  ["Part-time", "High", "Low"],
                  ["Fixed-term", "Medium", "Medium"],
                  ["Project-based", "Medium", "Medium"],
                  ["Independent contractor", "Low", "High"]
                ]
              }
            ],
          },
        ],
      },

      {
        title: "3. Working time, overtime & scheduling",
        content: [
          {
            type: "list",
            text: "Standard hours:",
            items: [
              "Normal workday: 8 hours",
              "Standard workweek: 40–48 hours",
              "Cultural familiarity with Western business norms",
              " Night shift work is common and legally permitted",
            ],
          },
          {
            type: "list",
            text: "Overtime:",
            items: [
              "Overtime pay is mandatory, not discretionary",
              {
                text: "Different premium rates apply for:",
                items: [
                  "Regular OT",
                  "Rest days",
                  "Holidays",
                  "Night shift differential"
                ]
              }
            ],
          },
          {
            type: "paragraph",
            text: "Overtime compliance is a frequent audit focus.",
          },
        ],
      },

      {
        title: "4. Leave entitlements & statutory absences",
        content: [
          {
            type: "list",
            text: "Annual leave:",
            items: [
              "Statutory minimum: 5 days Service Incentive Leave after 1 year",
              "Can be used for vacation or sick leave",
            ],
          },
          {
            type: "keyValue",
            keyText: "Market practice:",
            value: "Most competitive employers provide 10–15 days annual leave to support retention."
          },
          {
            type: "list",
            text: "Public holidays:",
            items: [
              "Approximately 12–13 paid regular and special holidays",
              "Approximately 12–13 paid regular and special holidays",
            ],
          },
          {
            type: "list",
            text: "Sick leave:",
            items: [
              "Statutory baseline is limited",
              "Many employers provide enhanced sick leave as a policy benefit",
            ],
          },
          {
            type: "list",
            text: "Maternity, paternity & parental leave:",
            items: [
              "Maternity leave: 105 days paid maternity leave - Funding primarily through social security, subject to eligibility - Applies regardless of civil status",
              "Paternity leave: 7 days paid paternity leave",
              "Solo parent leave: Additional statutory leave applies for eligible solo parents",
            ],
          },
        ],
      },
      {
        title: "5. Compensation structure & mandatory payments",
        content: [
          {
            type: "paragraph",
            text: "Salary benchmarks (USD / month):",
          },
          {
            type: "tableText",
            headers: ["Level", "Typical range"],
            rows: [
              ["Junior / entry", "$600 – $900"],
              ["Mid-level", "$900 – $1,400"],
              ["Senior / specialist", "$1,400 – $2,000+"],
            ]
          },
          {
            type: "list",
            text: "Salary is influenced by:",
            items: [
              "Metro Manila vs provincial location",
              "Night shift premiums",
              "Skill scarcity",
              "Employer brand and benefits",
            ],
          },
          {
            type: "keyValue",
            keyText: "13th-month pay (mandatory):",
            value: "The Philippines mandates 13th-month pay for rank-and-file employees."
          },
          {
            type: "list",
            text: "Key points:",
            items: [
              "Equal to 1/12 of total basic salary earned in the year",
              " Must be paid no later than December 24",
              "Skill scarcity",
              "Non-negotiable and not performance-based",
            ],
          },
          {
            type: "paragraph",
            text: "This is a structural payroll cost and must be built into total compensation planning.",
          },
        ],
      },
      {
        title: "6. Statutory contributions & on-costs",
        content: [
          {
            type: "list",
            text: "Employers must register and contribute to:",
            items: [
              "Social Security System (SSS)",
              "PhilHealth",
              "Pag-IBIG Fund",
            ],
          },
          {
            type: "list",
            text: "Typical employer on-cost:",
            items: [
              "Approximately 12–15% of gross salary",
              "Contribution caps apply, limiting cost escalation at higher salaries",
            ],
          },
          {
            type: "paragraph",
            text: "Payroll compliance infrastructure is mature but documentation accuracy is critical.",
          },
        ],
      },
      {
        title: "7. Termination, redundancy & dispute exposure",
        content: [
          {
            type: "list",
            text: "Termination during probation:",
            items: [
              "Easier than post-probation",
              {
                text: "Still requires:",
                items: [
                  "Documented process",
                  "Notice",
                ],
              },
            ],
          },
          {
            type: "list",
            text: "Termination after regularisation:",
            items: [
              "High-risk if mishandled",
              {
                text: "Requires:",
                items: [
                  "Substantive cause",
                  "Procedural due process",
                  "Written notices and opportunity to respond",
                ],
              },
            ],
          },
          {
            type: "paragraph",
            text: "Improper termination can result in reinstatement orders and significant back pay exposure.",
          },
          {
            type: "list",
            text: "Redundancy & retrenchment:",
            items: [
              "Permitted only where genuine",
              "Severance pay is mandatory",
              "Government notifications are required",
            ],
          },
          {
            type: "list",
            text: "Dispute & union risk:",
            items: [
              "Unionisation risk: moderate",
              "Labour disputes are not uncommon but manageable with good HR discipline",
            ],
          },
        ],
      },
      {
        title: "8. Talent landscape & capability depth",
        content: [
          {
            type: "list",
            text: "Core strengths:",
            items: [
              "Customer experience (voice & non-voice)",
              "Virtual assistance and executive support",
              "Finance operations and accounting support",
              "Admin, compliance, and operational roles",
              "Healthcare and insurance support services",
            ],
          },
          {
            type: "list",
            text: "Advanced and emerging capabilities:",
            items: [
              "RevOps and revenue enablement",
              "FP&A and financial analysis support",
              "AI-augmented professional roles",
              "Compliance, audit, and risk operations",
            ],
          },
          {
            type: "paragraph",
            text: "The Philippines increasingly supports AI-enabled hybrid roles, not just transactional work.",
          },
          {
            type: "list",
            text: "Talent depth & retention:",
            items: [
              "Talent depth: very deep",
              {
                text: "Retention: medium–high with:",
                items: [
                  "Career progression",
                  "Flexible work arrangements",
                  "Competitive benefits",
                ],
              },
            ],
          },
        ],
      },
      {
        title: "9. Language, culture & client alignment",
        content: [
          {
            type: "list",
            text: "",
            items: [
              "English proficiency: very high",
              "Accent neutrality: strong",
              "Cultural alignment: excellent with AU, US, UK",
              "Time zone: APAC-friendly with workable US overlap",
            ],
          },
          {
            type: "paragraph",
            text: "This makes the Philippines uniquely suited to voice CX and client-facing roles.",
          },
        ],
      },
      {
        title: "10. Overall assessment",
        content: [
          {
            type: "list",
            text: "Best suited for:",
            items: [
              "English proficiency: very high",
              "Accent neutrality: strong",
              "Cultural alignment: excellent with AU, US, UK",
              "Time zone: APAC-friendly with workable US overlap",
            ],
          },
          {
            type: "list",
            text: "Key watch-outs:",
            items: [
              "Labour law is protective and unforgiving of shortcuts",
              "Termination risk increases sharply after probation",
              "Mandatory 13th-month pay must be planned for",
            ],
          },
          {
            type: "keyValue",
            keyText: "Employer flexibility:",
            value: "Medium — high quality outcomes in exchange for disciplined compliance."
          }
        ],
      }
    ]
  },

  {
    country: "Sri Lanka",
    guide: [
      {
        title: "1. Country overview & strategic context",
        content: [
          {
            type: "paragraph",
            text: "Sri Lanka is a smaller but increasingly capable global workforce market, best known for professional services support, finance/admin operations, and emerging tech delivery. Compared to the Philippines, it is typically lower scale but often strong on stability, professionalism, and loyalty when teams are well-managed.",
          },
          {
            type: "list",
            text: "The country's workforce model is shaped by:",
            items: [
              "A regulated labour framework (with strong reliance on specific Acts and industry rules)",
              "High literacy and solid English capability in professional segments",
              "A culture that generally aligns well with structured delivery, governance, and service standards",
              "A benefits system anchored by EPF/ETF retirement contributions",
              "A holiday calendar that can materially affect scheduling",
            ],
          },
          {
            type: "paragraph",
            text: "This combination makes Sri Lanka a medium execution-risk, medium compliance-discipline jurisdiction — usually very workable, but it benefits from clear documentation and good HR rhythm.",
          },
        ],
      },

      {
        title: "2. Employment & engagement model (Sri Lanka)",
        content: [
          {
            type: "paragraph",
            text: "Sri Lankan employment compliance is heavily influenced by the Shop & Office Employees Act (for many white-collar roles) plus other applicable laws/industry instruments. Classification still depends on the substance of the relationship (control, integration, exclusivity), so designing the model correctly upfront matters.",
          },

          {
            type: "subsection",
            title: "2.1 Regular (full-time) employment",
            content: [
              {
                type: "paragraph",
                text: "Regular employment is the most defensible and operationally stable model.",
              },
              {
                type: "list",
                text: "Key characteristics:",
                items: [
                  "Standard working time commonly built around an 8–9 hour day and a 45–48 hour week (role/industry dependent)",
                  "Statutory leave and holiday rules apply",
                  "EPF/ETF contributions apply for eligible employees (see Section 6)",
                ],
              },
              {
                type: "paragraph",
                text: "Termination is not as procedurally rigid as the Philippines in every scenario, but poor process can still trigger disputes, reinstatement risk in some contexts, and cost exposure.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.2 Probationary employment",
            content: [
              {
                type: "paragraph",
                text: "Probation is common and should be explicitly documented in the contract.",
              },
              {
                type: "keyValue",
                keyText: "Typical range:",
                value: "Market practice is often around 6 months, with some roles extending up to 12 months depending on seniority/terms."
              },
              {
                type: "list",
                text: "Practical reality:",
                items: [
                  {
                    text: "Probation can be a meaningful flexibility window, but it should still be managed with:",
                    items: [
                      "clear performance standards",
                      "evidence of feedback/coaching",
                      "clean written confirmation or exit documentation"
                    ]
                  }
                ],
              },
              {
                type: "paragraph",
                text: "Failure to clearly document probation standards or performance issues can result in the employee being deemed regular by default, even before six months.",
              },
              {
                type: "keyValue",
                keyText: "Practical reality:",
                value: "Probation is not a \"risk-free\" period. Poorly managed probation is a common source of disputes.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.3 Fixed-term employment",
            content: [
              {
                type: "paragraph",
                text: "Fixed-term contracts are legally recognised but heavily scrutinised.",
              },
              {
                type: "list",
                text: "Accepted use cases:",
                items: [
                  "Genuine time-bound work",
                  "Temporary cover",
                  "Project-specific engagements",
                ],
              },
              {
                type: "list",
                text: "Risk factors:",
                items: [
                  "Repeated renewals for the same role",
                  "Ongoing core business functions",
                  "Long-term exclusivity",
                ],
              },
              {
                type: "paragraph",
                text: "Where a fixed-term employee performs the same role continuously, courts may deem the role regular in nature, regardless of contract wording.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.4 Project-based employment",
            content: [
              {
                type: "list",
                text: "Project employment is permitted when:",
                items: [
                  "The project is clearly defined",
                  "The scope and duration are known at commencement",
                  "Employment ends upon project completion",
                ],
              },
              {
                type: "paragraph",
                text: "Documentation at the outset is critical. Vague or open-ended \"projects\" materially increase reclassification risk.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.5 Part-time employment",
            content: [
              {
                type: "paragraph",
                text: "Part-time employment is recognised under Philippine law.",
              },
              {
                type: "list",
                text: "Key points:",
                items: [
                  "Fewer working hours than full-time employees",
                  "Statutory benefits apply pro-rata",
                  "Labour protections still apply in full",
                ],
              },
              {
                type: "paragraph",
                text: "Part-time status does not reduce termination or due-process obligations.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.6 Independent contractors / consultants",
            content: [
              {
                type: "paragraph",
                text: "Independent contracting is legally permissible but carries high enforcement risk.",
              },
              {
                type: "list",
                text: "Authorities assess:",
                items: [
                  "Degree of control and supervision",
                  "Exclusivity",
                  "Integration into business operations",
                  "Economic dependence",
                ],
              },
              {
                type: "list",
                text: "Where a contractor functions like an employee in practice, reclassification can trigger:",
                items: [
                  "Back pay",
                  "Unpaid benefits",
                  "Reinstatement claims",
                ],
              },
            ],
          },
          {
            type: "subsection",
            title: "2.7 Employment model risk summary",
            content: [
              {
                type: "tableText",
                headers: ["Engagement type", "Legal certainty", "Risk level"],
                rows: [
                  ["Regular employment", "Very high", "Low"],
                  ["Probationary", "High (time-limited)", "Low–medium"],
                  ["Part-time", "High", "Low"],
                  ["Fixed-term", "Medium", "Medium"],
                  ["Project-based", "Medium", "Medium"],
                  ["Independent contractor", "Low", "High"]
                ]
              }
            ],
          },
        ],
      },

      {
        title: "3. Working time, overtime & scheduling",
        content: [
          {
            type: "list",
            text: "Standard hours:",
            items: [
              "Normal workday: 8 hours",
              "Standard workweek: 40–48 hours",
              "Cultural familiarity with Western business norms",
              " Night shift work is common and legally permitted",
            ],
          },
          {
            type: "list",
            text: "Overtime:",
            items: [
              "Overtime pay is mandatory, not discretionary",
              {
                text: "Different premium rates apply for:",
                items: [
                  "Regular OT",
                  "Rest days",
                  "Holidays",
                  "Night shift differential"
                ]
              }
            ],
          },
          {
            type: "paragraph",
            text: "Overtime compliance is a frequent audit focus.",
          },
        ],
      },

      {
        title: "4. Leave entitlements & statutory absences",
        content: [
          {
            type: "list",
            text: "Annual leave:",
            items: [
              "Statutory minimum: 5 days Service Incentive Leave after 1 year",
              "Can be used for vacation or sick leave",
            ],
          },
          {
            type: "keyValue",
            keyText: "Market practice:",
            value: "Most competitive employers provide 10–15 days annual leave to support retention."
          },
          {
            type: "list",
            text: "Public holidays:",
            items: [
              "Approximately 12–13 paid regular and special holidays",
              "Approximately 12–13 paid regular and special holidays",
            ],
          },
          {
            type: "list",
            text: "Sick leave:",
            items: [
              "Statutory baseline is limited",
              "Many employers provide enhanced sick leave as a policy benefit",
            ],
          },
          {
            type: "list",
            text: "Maternity, paternity & parental leave:",
            items: [
              "Maternity leave: 105 days paid maternity leave - Funding primarily through social security, subject to eligibility - Applies regardless of civil status",
              "Paternity leave: 7 days paid paternity leave",
              "Solo parent leave: Additional statutory leave applies for eligible solo parents",
            ],
          },
        ],
      },
      {
        title: "5. Compensation structure & mandatory payments",
        content: [
          {
            type: "paragraph",
            text: "Salary benchmarks (USD / month):",
          },
          {
            type: "tableText",
            headers: ["Level", "Typical range"],
            rows: [
              ["Junior / entry", "$600 – $900"],
              ["Mid-level", "$900 – $1,400"],
              ["Senior / specialist", "$1,400 – $2,000+"],
            ]
          },
          {
            type: "list",
            text: "Salary is influenced by:",
            items: [
              "Metro Manila vs provincial location",
              "Night shift premiums",
              "Skill scarcity",
              "Employer brand and benefits",
            ],
          },
          {
            type: "keyValue",
            keyText: "13th-month pay (mandatory):",
            value: "The Philippines mandates 13th-month pay for rank-and-file employees."
          },
          {
            type: "list",
            text: "Key points:",
            items: [
              "Equal to 1/12 of total basic salary earned in the year",
              " Must be paid no later than December 24",
              "Skill scarcity",
              "Non-negotiable and not performance-based",
            ],
          },
          {
            type: "paragraph",
            text: "This is a structural payroll cost and must be built into total compensation planning.",
          },
        ],
      },
      {
        title: "6. Statutory contributions & on-costs",
        content: [
          {
            type: "list",
            text: "Employers must register and contribute to:",
            items: [
              "Social Security System (SSS)",
              "PhilHealth",
              "Pag-IBIG Fund",
            ],
          },
          {
            type: "list",
            text: "Typical employer on-cost:",
            items: [
              "Approximately 12–15% of gross salary",
              "Contribution caps apply, limiting cost escalation at higher salaries",
            ],
          },
          {
            type: "paragraph",
            text: "Payroll compliance infrastructure is mature but documentation accuracy is critical.",
          },
        ],
      },
      {
        title: "7. Termination, redundancy & dispute exposure",
        content: [
          {
            type: "list",
            text: "Termination during probation:",
            items: [
              "Easier than post-probation",
              {
                text: "Still requires:",
                items: [
                  "Documented process",
                  "Notice",
                ],
              },
            ],
          },
          {
            type: "list",
            text: "Termination after regularisation:",
            items: [
              "High-risk if mishandled",
              {
                text: "Requires:",
                items: [
                  "Substantive cause",
                  "Procedural due process",
                  "Written notices and opportunity to respond",
                ],
              },
            ],
          },
          {
            type: "paragraph",
            text: "Improper termination can result in reinstatement orders and significant back pay exposure.",
          },
          {
            type: "list",
            text: "Redundancy & retrenchment:",
            items: [
              "Permitted only where genuine",
              "Severance pay is mandatory",
              "Government notifications are required",
            ],
          },
          {
            type: "list",
            text: "Dispute & union risk:",
            items: [
              "Unionisation risk: moderate",
              "Labour disputes are not uncommon but manageable with good HR discipline",
            ],
          },
        ],
      },
      {
        title: "8. Talent landscape & capability depth",
        content: [
          {
            type: "list",
            text: "Core strengths:",
            items: [
              "Customer experience (voice & non-voice)",
              "Virtual assistance and executive support",
              "Finance operations and accounting support",
              "Admin, compliance, and operational roles",
              "Healthcare and insurance support services",
            ],
          },
          {
            type: "list",
            text: "Advanced and emerging capabilities:",
            items: [
              "RevOps and revenue enablement",
              "FP&A and financial analysis support",
              "AI-augmented professional roles",
              "Compliance, audit, and risk operations",
            ],
          },
          {
            type: "paragraph",
            text: "The Philippines increasingly supports AI-enabled hybrid roles, not just transactional work.",
          },
          {
            type: "list",
            text: "Talent depth & retention:",
            items: [
              "Talent depth: very deep",
              {
                text: "Retention: medium–high with:",
                items: [
                  "Career progression",
                  "Flexible work arrangements",
                  "Competitive benefits",
                ],
              },
            ],
          },
        ],
      },
      {
        title: "9. Language, culture & client alignment",
        content: [
          {
            type: "list",
            text: "",
            items: [
              "English proficiency: very high",
              "Accent neutrality: strong",
              "Cultural alignment: excellent with AU, US, UK",
              "Time zone: APAC-friendly with workable US overlap",
            ],
          },
          {
            type: "paragraph",
            text: "This makes the Philippines uniquely suited to voice CX and client-facing roles.",
          },
        ],
      },
      {
        title: "10. Overall assessment",
        content: [
          {
            type: "list",
            text: "Best suited for:",
            items: [
              "English proficiency: very high",
              "Accent neutrality: strong",
              "Cultural alignment: excellent with AU, US, UK",
              "Time zone: APAC-friendly with workable US overlap",
            ],
          },
          {
            type: "list",
            text: "Key watch-outs:",
            items: [
              "Labour law is protective and unforgiving of shortcuts",
              "Termination risk increases sharply after probation",
              "Mandatory 13th-month pay must be planned for",
            ],
          },
          {
            type: "keyValue",
            keyText: "Employer flexibility:",
            value: "Medium — high quality outcomes in exchange for disciplined compliance."
          }
        ],
      }
    ]
  },

  {
    country: "India",
    guide: [
      {
        title: "1. Country overview & strategic context",
        content: [
          {
            type: "paragraph",
            text: "India is one of the world's largest and most complex global workforce markets. It offers unparalleled scale, skill diversity, and cost efficiency, but operates within a fragmented legal and regulatory framework that varies significantly by state, industry, and engagement model.",
          },
          {
            type: "list",
            text: "The country's workforce model is shaped by:",
            items: [
              "A regulated labour framework (with strong reliance on specific Acts and industry rules)",
              "High literacy and solid English capability in professional segments",
              "A culture that generally aligns well with structured delivery, governance, and service standards",
              "A benefits system anchored by EPF/ETF retirement contributions",
              "A holiday calendar that can materially affect scheduling",
            ],
          },
          {
            type: "paragraph",
            text: "This combination makes Sri Lanka a medium execution-risk, medium compliance-discipline jurisdiction — usually very workable, but it benefits from clear documentation and good HR rhythm.",
          },
        ],
      },

      {
        title: "2. Employment & engagement model (Sri Lanka)",
        content: [
          {
            type: "paragraph",
            text: "Sri Lankan employment compliance is heavily influenced by the Shop & Office Employees Act (for many white-collar roles) plus other applicable laws/industry instruments. Classification still depends on the substance of the relationship (control, integration, exclusivity), so designing the model correctly upfront matters.",
          },

          {
            type: "subsection",
            title: "2.1 Regular (full-time) employment",
            content: [
              {
                type: "paragraph",
                text: "Regular employment is the most defensible and operationally stable model.",
              },
              {
                type: "list",
                text: "Key characteristics:",
                items: [
                  "Standard working time commonly built around an 8–9 hour day and a 45–48 hour week (role/industry dependent)",
                  "Statutory leave and holiday rules apply",
                  "EPF/ETF contributions apply for eligible employees (see Section 6)",
                ],
              },
              {
                type: "paragraph",
                text: "Termination is not as procedurally rigid as the Philippines in every scenario, but poor process can still trigger disputes, reinstatement risk in some contexts, and cost exposure.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.2 Probationary employment",
            content: [
              {
                type: "paragraph",
                text: "Probation is common and should be explicitly documented in the contract.",
              },
              {
                type: "keyValue",
                keyText: "Typical range:",
                value: "Market practice is often around 6 months, with some roles extending up to 12 months depending on seniority/terms."
              },
              {
                type: "list",
                text: "Practical reality:",
                items: [
                  {
                    text: "Probation can be a meaningful flexibility window, but it should still be managed with:",
                    items: [
                      "clear performance standards",
                      "evidence of feedback/coaching",
                      "clean written confirmation or exit documentation"
                    ]
                  }
                ],
              },
              {
                type: "paragraph",
                text: "Failure to clearly document probation standards or performance issues can result in the employee being deemed regular by default, even before six months.",
              },
              {
                type: "keyValue",
                keyText: "Practical reality:",
                value: "Probation is not a \"risk-free\" period. Poorly managed probation is a common source of disputes.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.3 Fixed-term employment",
            content: [
              {
                type: "paragraph",
                text: "Fixed-term contracts are legally recognised but heavily scrutinised.",
              },
              {
                type: "list",
                text: "Accepted use cases:",
                items: [
                  "Genuine time-bound work",
                  "Temporary cover",
                  "Project-specific engagements",
                ],
              },
              {
                type: "list",
                text: "Risk factors:",
                items: [
                  "Repeated renewals for the same role",
                  "Ongoing core business functions",
                  "Long-term exclusivity",
                ],
              },
              {
                type: "paragraph",
                text: "Where a fixed-term employee performs the same role continuously, courts may deem the role regular in nature, regardless of contract wording.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.4 Project-based employment",
            content: [
              {
                type: "list",
                text: "Project employment is permitted when:",
                items: [
                  "The project is clearly defined",
                  "The scope and duration are known at commencement",
                  "Employment ends upon project completion",
                ],
              },
              {
                type: "paragraph",
                text: "Documentation at the outset is critical. Vague or open-ended \"projects\" materially increase reclassification risk.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.5 Part-time employment",
            content: [
              {
                type: "paragraph",
                text: "Part-time employment is recognised under Philippine law.",
              },
              {
                type: "list",
                text: "Key points:",
                items: [
                  "Fewer working hours than full-time employees",
                  "Statutory benefits apply pro-rata",
                  "Labour protections still apply in full",
                ],
              },
              {
                type: "paragraph",
                text: "Part-time status does not reduce termination or due-process obligations.",
              },
            ],
          },
          {
            type: "subsection",
            title: "2.6 Independent contractors / consultants",
            content: [
              {
                type: "paragraph",
                text: "Independent contracting is legally permissible but carries high enforcement risk.",
              },
              {
                type: "list",
                text: "Authorities assess:",
                items: [
                  "Degree of control and supervision",
                  "Exclusivity",
                  "Integration into business operations",
                  "Economic dependence",
                ],
              },
              {
                type: "list",
                text: "Where a contractor functions like an employee in practice, reclassification can trigger:",
                items: [
                  "Back pay",
                  "Unpaid benefits",
                  "Reinstatement claims",
                ],
              },
            ],
          },
          {
            type: "subsection",
            title: "2.7 Employment model risk summary",
            content: [
              {
                type: "tableText",
                headers: ["Engagement type", "Legal certainty", "Risk level"],
                rows: [
                  ["Regular employment", "Very high", "Low"],
                  ["Probationary", "High (time-limited)", "Low–medium"],
                  ["Part-time", "High", "Low"],
                  ["Fixed-term", "Medium", "Medium"],
                  ["Project-based", "Medium", "Medium"],
                  ["Independent contractor", "Low", "High"]
                ]
              }
            ],
          },
        ],
      },

      {
        title: "3. Working time, overtime & scheduling",
        content: [
          {
            type: "list",
            text: "Standard hours:",
            items: [
              "Normal workday: 8 hours",
              "Standard workweek: 40–48 hours",
              "Cultural familiarity with Western business norms",
              " Night shift work is common and legally permitted",
            ],
          },
          {
            type: "list",
            text: "Overtime:",
            items: [
              "Overtime pay is mandatory, not discretionary",
              {
                text: "Different premium rates apply for:",
                items: [
                  "Regular OT",
                  "Rest days",
                  "Holidays",
                  "Night shift differential"
                ]
              }
            ],
          },
          {
            type: "paragraph",
            text: "Overtime compliance is a frequent audit focus.",
          },
        ],
      },

      {
        title: "4. Leave entitlements & statutory absences",
        content: [
          {
            type: "list",
            text: "Annual leave:",
            items: [
              "Statutory minimum: 5 days Service Incentive Leave after 1 year",
              "Can be used for vacation or sick leave",
            ],
          },
          {
            type: "keyValue",
            keyText: "Market practice:",
            value: "Most competitive employers provide 10–15 days annual leave to support retention."
          },
          {
            type: "list",
            text: "Public holidays:",
            items: [
              "Approximately 12–13 paid regular and special holidays",
              "Approximately 12–13 paid regular and special holidays",
            ],
          },
          {
            type: "list",
            text: "Sick leave:",
            items: [
              "Statutory baseline is limited",
              "Many employers provide enhanced sick leave as a policy benefit",
            ],
          },
          {
            type: "list",
            text: "Maternity, paternity & parental leave:",
            items: [
              "Maternity leave: 105 days paid maternity leave - Funding primarily through social security, subject to eligibility - Applies regardless of civil status",
              "Paternity leave: 7 days paid paternity leave",
              "Solo parent leave: Additional statutory leave applies for eligible solo parents",
            ],
          },
        ],
      },
      {
        title: "5. Compensation structure & mandatory payments",
        content: [
          {
            type: "paragraph",
            text: "Salary benchmarks (USD / month):",
          },
          {
            type: "tableText",
            headers: ["Level", "Typical range"],
            rows: [
              ["Junior / entry", "$600 – $900"],
              ["Mid-level", "$900 – $1,400"],
              ["Senior / specialist", "$1,400 – $2,000+"],
            ]
          },
          {
            type: "list",
            text: "Salary is influenced by:",
            items: [
              "Metro Manila vs provincial location",
              "Night shift premiums",
              "Skill scarcity",
              "Employer brand and benefits",
            ],
          },
          {
            type: "keyValue",
            keyText: "13th-month pay (mandatory):",
            value: "The Philippines mandates 13th-month pay for rank-and-file employees."
          },
          {
            type: "list",
            text: "Key points:",
            items: [
              "Equal to 1/12 of total basic salary earned in the year",
              " Must be paid no later than December 24",
              "Skill scarcity",
              "Non-negotiable and not performance-based",
            ],
          },
          {
            type: "paragraph",
            text: "This is a structural payroll cost and must be built into total compensation planning.",
          },
        ],
      },
      {
        title: "6. Statutory contributions & on-costs",
        content: [
          {
            type: "list",
            text: "Employers must register and contribute to:",
            items: [
              "Social Security System (SSS)",
              "PhilHealth",
              "Pag-IBIG Fund",
            ],
          },
          {
            type: "list",
            text: "Typical employer on-cost:",
            items: [
              "Approximately 12–15% of gross salary",
              "Contribution caps apply, limiting cost escalation at higher salaries",
            ],
          },
          {
            type: "paragraph",
            text: "Payroll compliance infrastructure is mature but documentation accuracy is critical.",
          },
        ],
      },
      {
        title: "7. Termination, redundancy & dispute exposure",
        content: [
          {
            type: "list",
            text: "Termination during probation:",
            items: [
              "Easier than post-probation",
              {
                text: "Still requires:",
                items: [
                  "Documented process",
                  "Notice",
                ],
              },
            ],
          },
          {
            type: "list",
            text: "Termination after regularisation:",
            items: [
              "High-risk if mishandled",
              {
                text: "Requires:",
                items: [
                  "Substantive cause",
                  "Procedural due process",
                  "Written notices and opportunity to respond",
                ],
              },
            ],
          },
          {
            type: "paragraph",
            text: "Improper termination can result in reinstatement orders and significant back pay exposure.",
          },
          {
            type: "list",
            text: "Redundancy & retrenchment:",
            items: [
              "Permitted only where genuine",
              "Severance pay is mandatory",
              "Government notifications are required",
            ],
          },
          {
            type: "list",
            text: "Dispute & union risk:",
            items: [
              "Unionisation risk: moderate",
              "Labour disputes are not uncommon but manageable with good HR discipline",
            ],
          },
        ],
      },
      {
        title: "8. Talent landscape & capability depth",
        content: [
          {
            type: "list",
            text: "Core strengths:",
            items: [
              "Customer experience (voice & non-voice)",
              "Virtual assistance and executive support",
              "Finance operations and accounting support",
              "Admin, compliance, and operational roles",
              "Healthcare and insurance support services",
            ],
          },
          {
            type: "list",
            text: "Advanced and emerging capabilities:",
            items: [
              "RevOps and revenue enablement",
              "FP&A and financial analysis support",
              "AI-augmented professional roles",
              "Compliance, audit, and risk operations",
            ],
          },
          {
            type: "paragraph",
            text: "The Philippines increasingly supports AI-enabled hybrid roles, not just transactional work.",
          },
          {
            type: "list",
            text: "Talent depth & retention:",
            items: [
              "Talent depth: very deep",
              {
                text: "Retention: medium–high with:",
                items: [
                  "Career progression",
                  "Flexible work arrangements",
                  "Competitive benefits",
                ],
              },
            ],
          },
        ],
      },
      {
        title: "9. Language, culture & client alignment",
        content: [
          {
            type: "list",
            text: "",
            items: [
              "English proficiency: very high",
              "Accent neutrality: strong",
              "Cultural alignment: excellent with AU, US, UK",
              "Time zone: APAC-friendly with workable US overlap",
            ],
          },
          {
            type: "paragraph",
            text: "This makes the Philippines uniquely suited to voice CX and client-facing roles.",
          },
        ],
      },
      {
        title: "10. Overall assessment",
        content: [
          {
            type: "list",
            text: "Best suited for:",
            items: [
              "English proficiency: very high",
              "Accent neutrality: strong",
              "Cultural alignment: excellent with AU, US, UK",
              "Time zone: APAC-friendly with workable US overlap",
            ],
          },
          {
            type: "list",
            text: "Key watch-outs:",
            items: [
              "Labour law is protective and unforgiving of shortcuts",
              "Termination risk increases sharply after probation",
              "Mandatory 13th-month pay must be planned for",
            ],
          },
          {
            type: "keyValue",
            keyText: "Employer flexibility:",
            value: "Medium — high quality outcomes in exchange for disciplined compliance."
          }
        ],
      }
    ]
  },
];
